import React, { useState } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDemoData } from '@/hooks/useDemoData';
import BidModal from '../modals/BidModal';

export default function LoadsTab() {
  const { loads, bids } = useDemoData();
  const [bidModalLoadId, setBidModalLoadId] = useState<string | null>(null);
  
  const bidding = loads.filter(l => !l.carrierID);
  const active = loads.filter(l => l.carrierID && new Date(l.deliveryEnd) > new Date());
  const completed = loads.filter(l => l.carrierID && new Date(l.deliveryEnd) <= new Date());

  const getPrice = (id: string) => {
    const arr = bids.filter(b => b.loadID === id);
    if (!arr.length) return '-';
    return `$${Math.max(...arr.map(b => b.amount))}`;
  };

  const getBidStatus = (loadId: string) => {
    const userBids = bids.filter(b => b.loadID === loadId && b.carrierID === 'current-carrier');
    if (userBids.length === 0) return null;
    
    const latestBid = userBids.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    return latestBid.status;
  };

  const handleBid = (loadId: string) => {
    setBidModalLoadId(loadId);
  };

  const closeBidModal = () => {
    setBidModalLoadId(null);
  };

  const getActionButton = (load: any, sectionType: 'bidding' | 'active' | 'completed') => {
    if (sectionType === 'bidding') {
      const bidStatus = getBidStatus(load.id);
      if (bidStatus) {
        const variants = {
          'Pending': 'secondary',
          'Accepted': 'default',
          'Rejected': 'destructive'
        } as const;
        return <Badge variant={variants[bidStatus as keyof typeof variants]}>{bidStatus}</Badge>;
      }
      return <Button onClick={() => handleBid(load.id)}>Bid</Button>;
    }
    
    if (sectionType === 'active') {
      return <Badge variant="default">In Transit</Badge>;
    }
    
    return <Badge variant="outline">Completed</Badge>;
  };

  const section = (title: string, items: any[], sectionType: 'bidding' | 'active' | 'completed') => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {sectionType === 'bidding' && 'Available loads for bidding'}
          {sectionType === 'active' && 'Loads currently in transit'}
          {sectionType === 'completed' && 'Successfully completed loads'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No {sectionType === 'bidding' ? 'available' : sectionType} loads
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Load #</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Pieces</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Pickup Date</TableHead>
                <TableHead>Delivery Date</TableHead>
                <TableHead>Status/Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(l => (
                <TableRow key={l.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{l.id}</TableCell>
                  <TableCell>{l.source}</TableCell>
                  <TableCell>{l.destination}</TableCell>
                  <TableCell>{l.pieces}</TableCell>
                  <TableCell>{l.weight} lbs</TableCell>
                  <TableCell className="font-medium">{getPrice(l.id)}</TableCell>
                  <TableCell>{new Date(l.pickupStart).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(l.deliveryEnd).toLocaleDateString()}</TableCell>
                  <TableCell>{getActionButton(l, sectionType)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Load Management</h2>
            <p className="text-muted-foreground">View available loads, track shipments, and manage bids</p>
          </div>
          <div className="flex space-x-2">
            <Badge variant="secondary">{bidding.length} Available</Badge>
            <Badge variant="default">{active.length} Active</Badge>
            <Badge variant="outline">{completed.length} Completed</Badge>
          </div>
        </div>

        {section('Available for Bidding', bidding, 'bidding')}
        {section('In Transit', active, 'active')}
        {section('Completed Loads', completed, 'completed')}
      </div>

      {/* Bid Modal */}
      {bidModalLoadId && (
        <BidModal 
          loadId={bidModalLoadId} 
          onClose={closeBidModal} 
        />
      )}
    </>
  );
}